import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EmpreenderApps } from "../../files/AppsArray";
import { appsDaCentral } from "../../lib/appsDaCentral";
import { chamarAdmin, mensagemDeErro } from "../../lib/adminApi";

// Aba "Landing pages" do painel de métricas: a central nas LPs dos apps (voz e chat), separada dos
// números do suporte. Quem conversou, quem pediu atendente e por quê, e quem clicou para assinar
// depois de conversar. Assinatura concluída não aparece: acontece no Empreender Plus.

interface DadosLps {
  totais: {
    voz_conversas: number;
    voz_duracao_media_s: number;
    voz_custo_usd: number;
    voz_atendente: number;
    voz_whatsapp: number;
    chat_conversas: number;
    chat_perguntas: number;
    chat_atendente: number;
    chat_whatsapp: number;
    cliques: number;
    cliques_voz: number;
    cliques_chat: number;
  };
  porApp: { app: string; voz: number; chat: number; atendente: number; whatsapp: number; cliques: number }[];
  motivos: { quando: string; app: string; canal: "voz" | "chat"; como: string; motivo: string; assuntos: string; abriu_whatsapp: boolean }[];
  cliques: { quando: string; app: string; canal: string; texto: string | null; destino: string | null }[];
}

// Como a pessoa chegou ao atendente no chat (eventos "atendente" da central).
const COMO_PEDIU: Record<string, string> = {
  rodape: "pelo botão no fim do chat",
  cartao: "pelo cartão de ajuda",
  insistencia: "insistiu depois de respostas que não resolveram",
  detalhar: "ao detalhar a dúvida",
  sugerido: "a IA sugeriu o atendente",
  "pediu na conversa por voz": "pediu na conversa por voz",
};

const CANAL: Record<string, string> = { voz: "Voz", chat: "Chat", "voz+chat": "Voz e chat" };

const hojeMenos = (dias: number) => {
  const d = new Date(Date.now() - dias * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const pct = (parte: number, todo: number) => (todo ? Math.round((parte / todo) * 100) : null);
const fmtPct = (valor: number | null) => (valor === null ? "—" : `${valor}%`);
const fmtNum = (valor: number) => valor.toLocaleString("pt-BR");
const fmtDuracao = (s: number) => (s >= 60 ? `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s` : `${s} s`);
const nomeDoApp = (slug: string) =>
  EmpreenderApps.find((a) => a.slugamigavel === slug || a.slug === slug || a.sluges === slug)?.name || slug;
const slugsDoApp = (slug: string) => {
  const app = EmpreenderApps.find((a) => a.slug === slug);
  return app ? Array.from(new Set([app.slug, app.sluges])) : [slug];
};

const AdminLps: React.FC<{ aoSessaoVencer: () => void }> = ({ aoSessaoVencer }) => {
  const [inicio, setInicio] = useState(hojeMenos(29));
  const [fim, setFim] = useState(hojeMenos(0));
  const [app, setApp] = useState("");
  const [dados, setDados] = useState<DadosLps | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const apps = useMemo(() => appsDaCentral("pt"), []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await chamarAdmin("/api/metricas", { acao: "lps", inicio, fim, app: app ? slugsDoApp(app) : undefined });
      if (r.status === 401) return aoSessaoVencer();
      if (!r.ok) return setErro(mensagemDeErro(r.json?.erro, "Não foi possível carregar os números das landing pages."));
      setDados(r.json as DadosLps);
    } catch {
      setErro("Sem conexão com o servidor das métricas.");
    } finally {
      setCarregando(false);
    }
  }, [inicio, fim, app, aoSessaoVencer]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const t = dados?.totais;
  const conversas = t ? t.voz_conversas + t.chat_conversas : 0;
  const atendente = t ? t.voz_atendente + t.chat_atendente : 0;
  const whatsapp = t ? t.voz_whatsapp + t.chat_whatsapp : 0;

  return (
    <>
      <section className="metricas-filtros">
        <div className="metricas-rapidos">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => {
                setInicio(hojeMenos(d - 1));
                setFim(hojeMenos(0));
              }}
              className={inicio === hojeMenos(d - 1) && fim === hojeMenos(0) ? "ativo" : ""}
            >
              {d} dias
            </button>
          ))}
        </div>
        <label>
          De <input type="date" value={inicio} max={fim} onChange={(e) => setInicio(e.target.value)} />
        </label>
        <label>
          Até <input type="date" value={fim} min={inicio} onChange={(e) => setFim(e.target.value)} />
        </label>
        <label>
          App
          <select value={app} onChange={(e) => setApp(e.target.value)}>
            <option value="">Todos</option>
            {apps.map((a) => (
              <option key={a.slugamigavel} value={a.slug}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        {carregando && <span className="metricas-carregando">Atualizando…</span>}
      </section>

      {erro && <p className="metricas-erro">{erro}</p>}

      {dados && t && (
        <>
          <section className="metricas-cartoes">
            <div className="metricas-cartao destaque">
              <span>Clicaram para assinar</span>
              <strong>{fmtNum(t.cliques)}</strong>
              <small>
                {fmtPct(pct(t.cliques, conversas))} das conversas · {fmtNum(t.cliques_voz)} pela voz · {fmtNum(t.cliques_chat)} pelo chat
              </small>
            </div>
            <div className="metricas-cartao">
              <span>Conversas nas LPs</span>
              <strong>{fmtNum(conversas)}</strong>
              <small>
                {fmtNum(t.voz_conversas)} por voz · {fmtNum(t.chat_conversas)} no chat
              </small>
            </div>
            <div className="metricas-cartao">
              <span>Pediram atendente</span>
              <strong>{fmtPct(pct(atendente, conversas))}</strong>
              <small>
                {fmtNum(atendente)} conversas · {fmtNum(t.voz_atendente)} voz · {fmtNum(t.chat_atendente)} chat
              </small>
            </div>
            <div className="metricas-cartao">
              <span>Abriram o WhatsApp</span>
              <strong>{fmtNum(whatsapp)}</strong>
              <small>{fmtPct(pct(whatsapp, atendente))} de quem pediu atendente</small>
            </div>
            <div className="metricas-cartao">
              <span>Voz</span>
              <strong>{t.voz_conversas ? fmtDuracao(t.voz_duracao_media_s) : "—"}</strong>
              <small>duração média · US$ {t.voz_custo_usd.toFixed(2).replace(".", ",")} no período</small>
            </div>
          </section>

          <section className="metricas-bloco">
            <h2>Por app</h2>
            <table className="metricas-tabela">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Voz</th>
                  <th>Chat</th>
                  <th>Pediram atendente</th>
                  <th>WhatsApp</th>
                  <th>Clicaram para assinar</th>
                </tr>
              </thead>
              <tbody>
                {dados.porApp.map((a) => (
                  <tr key={a.app}>
                    <td>{nomeDoApp(a.app)}</td>
                    <td>{fmtNum(a.voz)}</td>
                    <td>{fmtNum(a.chat)}</td>
                    <td>
                      {fmtNum(a.atendente)} <small>({fmtPct(pct(a.atendente, a.voz + a.chat))})</small>
                    </td>
                    <td>{fmtNum(a.whatsapp)}</td>
                    <td>
                      {fmtNum(a.cliques)} <small>({fmtPct(pct(a.cliques, a.voz + a.chat))})</small>
                    </td>
                  </tr>
                ))}
                {!dados.porApp.length && (
                  <tr>
                    <td colSpan={6} className="metricas-vazio">
                      Sem conversas nas landing pages no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <div className="metricas-duas">
            <section className="metricas-bloco">
              <h2>Por que pediram atendente</h2>
              <p className="metricas-legenda">
                Na voz, a dúvida tirada da conversa (a mesma que vai na mensagem do WhatsApp); no chat, o caso que foi para o WhatsApp ou a
                última pergunta antes do pedido.
              </p>
              <ol className="metricas-lista">
                {dados.motivos.map((m, i) => (
                  <li key={`${m.quando}-${i}`}>
                    <span className="metricas-contagem">{CANAL[m.canal] || m.canal}</span>
                    <div className="metricas-item">
                      <p>{m.motivo || "Sem dúvida específica: só pediu para falar com alguém."}</p>
                      <small>
                        {nomeDoApp(m.app)} · {m.quando}
                        {m.como && ` · ${COMO_PEDIU[m.como] || m.como}`}
                        {m.assuntos && ` · já tinha visto: ${m.assuntos}`}
                        {m.abriu_whatsapp && <span className="metricas-selo">abriu o WhatsApp</span>}
                      </small>
                    </div>
                  </li>
                ))}
                {!dados.motivos.length && <li className="metricas-vazio">Ninguém pediu atendente no período.</li>}
              </ol>
            </section>

            <section className="metricas-bloco">
              <h2>Cliques para assinar</h2>
              <p className="metricas-legenda">
                Botões de cadastro ou de plano clicados depois de conversar com a central. A assinatura em si acontece no Empreender Plus e não
                aparece aqui.
              </p>
              <ol className="metricas-lista">
                {dados.cliques.map((c, i) => (
                  <li key={`${c.quando}-${i}`}>
                    <span className="metricas-contagem">{CANAL[c.canal] || c.canal}</span>
                    <div className="metricas-item">
                      <p>{c.texto ? `"${c.texto}"` : "Botão de cadastro"}</p>
                      <small>
                        {nomeDoApp(c.app)} · {c.quando}
                        {c.destino && ` · ${c.destino}`}
                      </small>
                    </div>
                  </li>
                ))}
                {!dados.cliques.length && <li className="metricas-vazio">Nenhum clique para assinar no período.</li>}
              </ol>
            </section>
          </div>
        </>
      )}
    </>
  );
};

export default AdminLps;
